import { CommonMessage } from '@frontend/lang';
import React from 'react';
import { LuX } from 'react-icons/lu';

import { RequiredFlag } from '../required-flag';
import useTextInput from './text-input.controller';

const ID = 'text-input';
export interface TextInputProps {
    useConditionedStyling?: boolean;
    prepend?: number;
    required?: boolean;
    valid?: boolean;
    isValidCallback?: (valid: boolean) => void;
    onChange?: (value: string) => void;
    onFocusChange?: () => void;
    value?: string | null;
    errorMessage?: React.ReactNode;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;
    autoFocus?: boolean;
    disabled?: boolean;
    submitted?: boolean;
    id?: string;
    placeholder?: string;
    isClearable?: boolean;
    isLoading?: boolean;

    className?: string;
    onBlur?: () => void;
    defaultValue?: string;
    inputClassName?: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    inputMode?: 'search' | 'text' | 'email' | 'tel' | 'url' | 'none' | 'numeric' | 'decimal';
    customLabelAction?: React.ReactNode;
    maxLength?: number;
}

export const TextInput = (props: TextInputProps) => {
    const viewProps = useTextInput(props);

    function getErrorMessage(): React.ReactNode {
        if (props.required) {
            return <>{props.label} is required</>;
        }
        return <>An error occurred with field {props.label}</>;
    }

    return (
        <div
            id={ID}
            className={'flex h-full w-full flex-col items-start justify-start gap-1 ' + props.className}>
            {props.label && (
                <div className='mb-0! flex w-full flex-col'>
                    <div className='flex justify-between'>
                        <div className='inline-flex items-center gap-1'>
                            <span className='text-foreground-default text-sm font-medium'>{props.label}</span>
                            <RequiredFlag required={props.required} />
                        </div>
                        {props.customLabelAction}
                    </div>
                    {props.helpMessage && (
                        <>
                            <small className='form-text text-muted'>{props.helpMessage}</small>
                        </>
                    )}
                </div>
            )}

            <div className='inline-flex h-full w-full flex-row items-start justify-start self-stretch'>
                {props.prepend !== undefined && (
                    <div className='bg-background-surface border-border-default flex h-11 items-center justify-start gap-2 overflow-hidden rounded-tl-md rounded-bl-md border-t border-b border-l px-4'>
                        <div className='text-foreground-default justify-start text-base font-medium'>{props.prepend}</div>
                    </div>
                )}
                <div
                    className={`${props.inputClassName ? props.inputClassName : `bg-background-surface h-11 w-full flex-1 ${props.prepend === undefined ? 'rounded-md' : 'rounded-tr-md rounded-br-md'} outline-border-default flex items-center justify-start gap-2 overflow-hidden outline-1 outline-offset-[-1px]`}`}>
                    <input
                        onBlur={props.onBlur ?? viewProps.onBlur}
                        className={`form-control ${viewProps.valid === false && viewProps.touched ? 'border-status-danger border' : 'border-none outline-none'} text-base font-medium`}
                        autoFocus={props.autoFocus}
                        id={props.id}
                        data-testid={props.id}
                        value={props.value ?? undefined}
                        type='text'
                        defaultValue={props.defaultValue}
                        placeholder={props.placeholder}
                        onFocus={() => {
                            viewProps.changeTouched(true);
                            props.onFocusChange && props.onFocusChange();
                        }}
                        required={props.required}
                        onChange={(event) => {
                            if (props.onChange) props.onChange(event.target.value);
                            viewProps.isValid();
                        }}
                        onKeyDown={props.onKeyDown}
                        disabled={props.disabled}
                        inputMode={props.inputMode}
                        maxLength={props.maxLength}
                    />

                    {props.isLoading && (
                        <div
                            className='me-3'
                            role='status'>
                            <svg
                                aria-hidden='true'
                                className='fill-brand-primary text-brand-light size-4 animate-spin'
                                viewBox='0 0 100 101'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'>
                                <path
                                    d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
                                    fill='currentColor'
                                />
                                <path
                                    d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
                                    fill='currentFill'
                                />
                            </svg>
                            <span className='sr-only'>{CommonMessage.FORMS.LOADING_PLACEHOLDER}</span>
                        </div>
                    )}

                    {props.isClearable && props.value && (
                        <div
                            className='text-brand-muted me-3'
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation();
                                props.onChange && props.onChange('');
                            }}
                            id={props.id || ID + '-clear-button'}>
                            <LuX />
                        </div>
                    )}
                </div>
            </div>
            {viewProps.valid === false && viewProps.touched === true ? (
                <div className='text-status-danger font-medium'>
                    <small>{props.errorMessage ?? getErrorMessage()}</small>
                </div>
            ) : (
                <></>
            )}
        </div>
    );
};
